import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPenToSquare,
    faTrash,
    faEye,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";
import { Toaster, toast } from "react-hot-toast";
import { PropagateLoader } from "react-spinners";
import { TablePagination } from "@mui/material";
import { Base_URL } from "actions/Base_URL";

export default function Inventory() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");
    const [loading, setLoading] = useState(false);
    const [labData, setLabData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedLab, setSelectedLab] = useState("");

    // Helper function to get lab name from lab ID
    const getLabName = (labId) => {
        const lab = labData.find((lab) => lab.lab_id === labId);
        return lab ? lab.lab_name : "Unknown Lab";
    };

    const fetchLabs = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${Base_URL}/labs/getAllLabs`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const allLabs = response.data.data || [];
            setLabData(allLabs);

            // AUTO-RESTRICT for Lab Incharge
            const storedLabId = localStorage.getItem("lab_id");
            if (storedLabId) {
                setSelectedLab(storedLabId);
            } else if (
                !selectedLab &&
                allLabs.length > 0
            ) {
                setSelectedLab(allLabs[0].lab_id);
            }
        } catch (error) {
            toast.error("Failed to fetch labs");
        }
    };

    useEffect(() => {
        fetchLabs();
    }, []);

    useEffect(() => {
        if (selectedLab || localStorage.getItem("lab_id")) {
            getTableData(currentPage, itemsPerPage, selectedLab || localStorage.getItem("lab_id"));
        }
    }, [currentPage, itemsPerPage, selectedLab]);

    // Handle search with debouncing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm.trim()) {
                handleSearch();
            } else {
                getTableData(currentPage, itemsPerPage, selectedLab);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const handleSearch = async () => {
        if (searchTerm.trim()) {
            setLoading(true);
            try {
                const loginToken = localStorage.getItem("token");
                let url = `${Base_URL}/api/inventory/searchInventories?query=${encodeURIComponent(
                    searchTerm
                )}`;
                if (selectedLab) {
                    url += `&lab_id=${selectedLab}`;
                }

                const getApiResponse = await axios.get(url, {
                    headers: {
                        Authorization: `Bearer ${loginToken}`,
                        "Content-Type": "application/json",
                    },
                });
                if (getApiResponse.status === 200) {
                    const responseData = getApiResponse.data;
                    setData(responseData.inventories || []);
                    setTotalItems(responseData.total || 0);
                }
            } catch (error) {
                toast.error("Error searching inventory: " + error.message);
            } finally {
                setLoading(false);
            }
        } else {
            getTableData(currentPage, itemsPerPage, selectedLab);
        }
    };

    const handleSort = (column) => {
        setSortOrder((prevOrder) =>
            sortColumn === column && prevOrder === "asc" ? "desc" : "asc"
        );
        setSortColumn(column);
    };

    async function getTableData(page = 1, limit = 10, labId = "") {
        setLoading(true);
        try {
            const loginToken = localStorage.getItem("token");
            let url = `${Base_URL}/api/inventory/getAllInventories?page=${page}&limit=${limit}`;
            if (labId) {
                url += `&lab_id=${labId}`;
            }

            const getApiResponse = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${loginToken}`,
                    "Content-Type": "application/json",
                },
            });
            if (getApiResponse.status === 200) {
                const responseData = getApiResponse.data;
                // Handle both old array format and new paginated object format
                const inventories = responseData.inventories || (Array.isArray(responseData) ? responseData : []);
                const total = responseData.total !== undefined ? responseData.total : (Array.isArray(responseData) ? responseData.length : 0);
                
                setData(inventories);
                setTotalItems(total);
            }

        } catch (error) {
            toast.error("Error fetching inventory: " + error.message);
        } finally {
            setLoading(false);
        }
    }

    async function deleteInventory(id) {
        try {
            const loginToken = localStorage.getItem("token");
            const getApiResponse = await axios.delete(
                `${Base_URL}/api/inventory/deleteInventory/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${loginToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (getApiResponse.status === 200) {
                getTableData(currentPage, itemsPerPage, selectedLab);
            }
        } catch (error) {
            toast.error("Error deleting inventory: " + error.message);
        }
    }

    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you really want to delete these records?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: ReactDOMServer.renderToString(
                <>
                    Yes, delete it!
                    <FontAwesomeIcon icon={faTrash} style={{ marginLeft: "10px" }} />
                </>
            ),
            allowOutsideClick: false,
            allowEscapeKey: false,
        }).then((result) => {
            if (result.isConfirmed) {
                deleteInventory(id);
                Swal.fire({
                    title: "Deleted!",
                    text: "Inventory record deleted successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
        });
    };
    const userId = localStorage.getItem("user_id");

    return (
        <>
            <Toaster position="bottom-left" reverseOrder={false} />
            <div className="relative">
                <div className="flex justify-between items-center px-4 mt-6">
                    <p className="font-semibold text-lg text-white">Inventory Details</p>
                    <Tooltip title=" Create Inventory" arrow>
                        <button
                            className="bg-lightBlue-500 mb-0 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                // CHANGED: Admin route -> LabIncharge route
                                navigate(`/labincharge/createInventory`, {
                                    state: { type: "create", id: userId },
                                });
                            }}
                        >
                            Create Inventory
                        </button>
                    </Tooltip>
                </div>
                <div className="px-6 mt-4 mb-6 ">
                    <div className="flex flex-wrap gap-4 items-center">
                        {/* Lab Filter */}
                        {!localStorage.getItem("lab_id") && (
                            <div className="flex-1 min-w-[200px]">
                                <select
                                    value={selectedLab}
                                    onChange={(e) => {
                                        setSelectedLab(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="border-0 px-3 py-3 bg-blueGray-700 text-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                                >
                                    <option value="">All Labs</option>
                                    {(labData || []).map((lab) => (
                                        <option key={lab.lab_id} value={lab.lab_id}>
                                            {lab.lab_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Search */}
                        <div className="flex-1 min-w-[300px]">
                            <div className="relative flex w-full flex-wrap items-stretch">
                                <span className="z-10 h-full leading-snug font-normal absolute text-center text-white bg-transparent rounded text-base items-center justify-center w-8 pl-3 py-3">
                                    <i className="fas fa-search"></i>
                                </span>
                                <Tooltip title="Search Record" arrow>
                                    <input
                                        type="text"
                                        placeholder="Search by Inventory Name"
                                        className="border-0 px-3 py-3 placeholder-blueGray-400 text-white relative bg-blueGray-700 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full pl-10 pr-10"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </Tooltip>
                                {searchTerm && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchTerm("")}
                                        className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-10 text-white hover:text-blueGray-300 focus:outline-none"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <PropagateLoader color="#141c52" size={18} />
                    </div>
                ) : (
                    <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-700">
                        <div className="block w-full overflow-x-auto">
                            <table className="items-center w-full bg-transparent border-collapse">
                                <thead>
                                    <tr>
                                        <th className="px-6 min-w-[200px] border border-solid py-3 text-sm font-bold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Sr.No
                                        </th>
                                        <th
                                            className="px-6 min-w-[200px] border border-solid py-3 text-sm font-bold text-left bg-blueGray-700 text-white border-blueGray-600"
                                            onClick={() => handleSort("name")}
                                        >
                                            Inventory Name{" "}
                                            {sortColumn === "name"
                                                ? sortOrder === "asc"
                                                    ? "↑"
                                                    : "↓"
                                                : ""}
                                        </th>
                                        <th
                                            className="px-6 min-w-[200px] border border-solid py-3 text-sm font-bold text-left bg-blueGray-700 text-white border-blueGray-600"
                                            onClick={() => handleSort("lab_name")}
                                        >
                                            Lab Name{" "}
                                            {sortColumn === "lab_name"
                                                ? sortOrder === "asc"
                                                    ? "↑"
                                                    : "↓"
                                                : ""}
                                        </th>
                                        <th className="px-6 min-w-[150px] border border-solid py-3 text-sm font-bold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Description
                                        </th>
                                        <th className="px-6 min-w-[100px] border border-solid py-3 text-sm font-bold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Quantity
                                        </th>
                                        <th className="px-6 min-w-[200px] border border-solid py-3 text-sm font-bold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data || []).length ? (
                                        (data || []).map((item, index) => (
                                            <tr key={item.id}>
                                                <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left border-blueGray-700">
                                                    <span className="ml-3 font-bold text-white">
                                                        {(currentPage - 1) * itemsPerPage + index + 1}
                                                    </span>
                                                </td>
                                                <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left border-blueGray-700">
                                                    <span className="ml-3 font-bold text-white capitalize">
                                                        {item.name}
                                                    </span>
                                                </td>
                                                <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 border-blueGray-700">
                                                    <span className="ml-3 font-bold text-white">
                                                        {getLabName(item.lab_id) || "-"}
                                                    </span>
                                                </td>
                                                <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs p-4 border-blueGray-700">
                                                    <span
                                                        className="ml-3 font-bold text-white truncate block max-w-[150px]"
                                                        title={item.description}
                                                    >
                                                        {item.description || "-"}
                                                    </span>
                                                </td>
                                                <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 border-blueGray-700">
                                                    <span className="ml-3 font-bold text-white">
                                                        {item.quantity || "-"}
                                                    </span>
                                                </td>
                                                <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-center flex gap-4">
                                                    <Tooltip title="Edit" arrow>
                                                        <motion.button
                                                            whileHover={{ scale: 1.2, color: "#2563EB" }}
                                                            whileTap={{ scale: 0.9 }}
                                                            type="button"
                                                            onClick={() => {
                                                                // CHANGED: Admin route -> LabIncharge route
                                                                navigate(`/labincharge/createInventory`, {
                                                                    state: { type: "edit", id: item.id },
                                                                });
                                                            }}
                                                            className="text-blue-500 hover:text-blue-700 focus:outline-none"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faPenToSquare}
                                                                className="text-xl"
                                                            />
                                                        </motion.button>
                                                    </Tooltip>
                                                    <Tooltip title="Delete" arrow>
                                                        <motion.button
                                                            whileHover={{ scale: 1.2, color: "#DC2626" }}
                                                            whileTap={{ scale: 0.9 }}
                                                            type="button"
                                                            onClick={() => handleDelete(item.id)}
                                                            className="text-blue-500 hover:text-red-700 focus:outline-none mx-4"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                                className="text-xl"
                                                            />
                                                        </motion.button>
                                                    </Tooltip>
                                                    <Tooltip title="View" arrow>
                                                        <motion.button
                                                            whileHover={{ scale: 1.2, color: "#FACC15" }}
                                                            whileTap={{ scale: 0.9 }}
                                                            type="button"
                                                            onClick={() => {
                                                                // CHANGED: Admin route -> LabIncharge route
                                                                navigate(`/labincharge/createInventory`, {
                                                                    state: { type: "view", id: item.id },
                                                                });
                                                            }}
                                                            className="text-blue-500 focus:outline-none mx-3 hover:text-yellow-500"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faEye}
                                                                className="text-xl"
                                                            />
                                                        </motion.button>
                                                    </Tooltip>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="text-1xl text-center align-middle bg-blueGray-700 text-white font-semibold p-4"
                                            >
                                                No Data Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            {data.length > 0 && (
                                <div className="flex justify-center mt-4 bg-blueGray-700 p-4 rounded-lg">
                                    <TablePagination
                                        component="div"
                                        count={totalItems}
                                        page={currentPage - 1}
                                        onPageChange={(event, newPage) =>
                                            setCurrentPage(newPage + 1)
                                        }
                                        rowsPerPage={itemsPerPage}
                                        onRowsPerPageChange={(event) => {
                                            setItemsPerPage(parseInt(event.target.value, 10));
                                            setCurrentPage(1);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        labelRowsPerPage="Items per page"
                                        sx={{
                                            "& .MuiTablePagination-toolbar": {
                                                fontWeight: 500,
                                                fontSize: "0.75rem",
                                                color: "white",
                                            },
                                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                                            {
                                                fontWeight: 500,
                                                fontSize: "0.75rem",
                                                color: "white",
                                            },
                                            "& .MuiTablePagination-select": {
                                                fontWeight: 500,
                                                fontSize: "0.75rem",
                                                color: "white",
                                            },
                                            "& .MuiTablePagination-selectIcon": {
                                                color: "white",
                                            },
                                            "& .MuiIconButton-root": {
                                                color: "white",
                                            },
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
